import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthShell, AuthError } from '../components/auth/AuthShell';
import { useAuthStore } from '../store/useAuthStore';
import { useI18n } from '../contexts/I18nContext';
import * as authApi from '../lib/authApi';
import { isStrongPassword } from './ResetPasswordPage';

type InviteState =
  | { status: 'loading' }
  | { status: 'invalid' }
  | { status: 'valid'; email: string; name: string };

export function AcceptInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { t } = useI18n();

  const [invite, setInvite] = useState<InviteState>({ status: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Peek (non-consuming) validation so we can greet the invitee by name.
    authApi
      .getInvite(token)
      .then((data) => {
        if (!cancelled) setInvite({ status: 'valid', email: data.email, name: data.name });
      })
      .catch(() => {
        if (!cancelled) setInvite({ status: 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t('auth.reset.mismatch'));
      return;
    }
    if (!isStrongPassword(password)) {
      setError(t('auth.reset.weak'));
      return;
    }
    setSubmitting(true);
    try {
      const session = await authApi.acceptInvite(token, password);
      setSession(session);
      // Every user lands on the daily summary after login (CLE-448).
      navigate(authApi.postAuthLandingPath(), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.invite.invalid'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title={t('auth.invite.title')}>
      {invite.status === 'loading' && (
        <p className="text-sm" style={{ color: '#94A3B8' }}>
          {t('auth.invite.loading')}
        </p>
      )}

      {invite.status === 'invalid' && (
        <>
          <AuthError message={t('auth.invite.invalid')} />
          <p className="text-center text-sm" style={{ color: '#64748B' }}>
            <Link to="/login" style={{ color: '#A78BFA' }} className="underline">
              {t('auth.forgot.backToLogin')}
            </Link>
          </p>
        </>
      )}

      {invite.status === 'valid' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm" style={{ color: '#94A3B8' }}>
            {t('auth.invite.greeting', { name: invite.name, email: invite.email })}
          </p>

          {error && <AuthError message={error} />}

          <label className="block text-sm">
            <span style={{ color: '#94A3B8' }}>{t('auth.invite.password')}</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-field mt-1 w-full px-3 py-2 outline-none"
            />
          </label>

          <label className="block text-sm">
            <span style={{ color: '#94A3B8' }}>{t('auth.invite.confirm')}</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="login-field mt-1 w-full px-3 py-2 outline-none"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="login-submit w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? t('auth.invite.submitting') : t('auth.invite.submit')}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
